/**
 * Settings sidebar for tenant AI provider configuration.
 * Fetches/saves company-level store_settings (AI fields) via GET/POST /store-settings.
 */
import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';

const PROVIDERS = [
  { value: 'openai', labelEn: 'OpenAI (GPT)', labelAr: 'OpenAI (GPT)' },
  { value: 'anthropic', labelEn: 'Anthropic (Claude)', labelAr: 'Anthropic (Claude)' },
  { value: 'gemini', labelEn: 'Google (Gemini)', labelAr: 'Google (Gemini)' },
  { value: 'deepseek', labelEn: 'DeepSeek', labelAr: 'DeepSeek' },
  { value: 'ollama', labelEn: 'Ollama (local)', labelAr: 'Ollama (محلي)' },
  { value: 'local', labelEn: 'Local / Custom', labelAr: 'محلي / مخصص' },
  { value: 'openrouter', labelEn: 'OpenRouter', labelAr: 'OpenRouter' },
];

const DEFAULT_BASE_URLS = {
  openai: 'https://api.openai.com/v1',
  anthropic: 'https://api.anthropic.com',
  gemini: '',
  deepseek: 'https://api.deepseek.com/v1',
  ollama: 'http://localhost:11434/v1',
  local: 'http://localhost:11434/v1',
  openrouter: 'https://openrouter.ai/api/v1',
};

const DEFAULT_MODELS = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-3-5-sonnet-20241022',
  gemini: 'gemini-1.5-flash',
  deepseek: 'deepseek-chat',
  ollama: 'llama3',
  local: 'llama3',
  openrouter: 'openai/gpt-4o-mini',
};

const USER_INSTRUCTIONS_MAX = 2000;

export default function ProviderSettingsSidebar({ open: isOpen, onClose, t, dir, creativeMode = false, onCreativeModeChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [userInstructions, setUserInstructions] = useState('');
  const [userInstructionsLoading, setUserInstructionsLoading] = useState(false);
  const [userInstructionsSaving, setUserInstructionsSaving] = useState(false);
  const [userInstructionsMessage, setUserInstructionsMessage] = useState(null);
  const [form, setForm] = useState({
    aiEnabled: true,
    aiProvider: 'openai',
    aiApiKey: '',
    aiApiBaseUrl: '',
    aiDefaultModel: '',
  });
  const [showApiKey, setShowApiKey] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get('/store-settings', {
        params: { useCompanyDefault: '1', includeApiKey: '1' },
        timeout: 10000,
      });
      const data = res.data;
      const s = data?.settings ?? {};
      const provider = (s.aiProvider || 'openai').toString().toLowerCase();
      setSettings(data);
      setForm({
        aiEnabled: Boolean(s.aiEnabled),
        aiProvider: provider,
        aiApiKey: (s.aiApiKey || '').trim(),
        aiApiBaseUrl: (s.aiApiBaseUrl || '').trim() || DEFAULT_BASE_URLS[provider] || DEFAULT_BASE_URLS.openai,
        aiDefaultModel: (s.aiDefaultModel || '').trim() || DEFAULT_MODELS[provider] || 'gpt-4o-mini',
      });
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Failed to load settings';
      setError(msg);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchSettings();
  }, [isOpen, fetchSettings]);

  // User instructions: stored via /api/orion/preferences (scope=user). Backend enforces user-scope only.
  useEffect(() => {
    if (!isOpen) return;
    setUserInstructionsLoading(true);
    client.get('/api/orion/preferences', { params: { scope: 'user' }, timeout: 8000 })
      .then((r) => {
        const prefs = r.data?.preferences ?? [];
        const instr = prefs.find((p) => p.key_name === 'user_instructions')?.value_json;
        setUserInstructions(typeof instr === 'string' ? instr : '');
      })
      .catch(() => setUserInstructions(''))
      .finally(() => setUserInstructionsLoading(false));
  }, [isOpen]);

  const handleSaveUserInstructions = async () => {
    const val = (userInstructions || '').trim().slice(0, USER_INSTRUCTIONS_MAX);
    setUserInstructionsSaving(true);
    setUserInstructionsMessage(null);
    try {
      await client.post('/api/orion/preferences', {
        scope: 'user',
        key_name: 'user_instructions',
        value_json: val,
      });
      setUserInstructionsMessage(t('userInstructionsSaved'));
      setTimeout(() => setUserInstructionsMessage(null), 2500);
    } catch (err) {
      setUserInstructionsMessage(t('userInstructionsSaveFailed'));
    } finally {
      setUserInstructionsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'aiProvider') {
        next.aiApiBaseUrl = (DEFAULT_BASE_URLS[value] || prev.aiApiBaseUrl || '').trim() || DEFAULT_BASE_URLS.openai;
        if (!(prev.aiDefaultModel || '').trim()) next.aiDefaultModel = DEFAULT_MODELS[value] || DEFAULT_MODELS.openai;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const current = settings?.settings || {};
      const payload = {
        ...current,
        aiEnabled: form.aiEnabled,
        aiProvider: form.aiProvider,
        aiApiKey: (form.aiApiKey || '').trim(),
        aiApiBaseUrl: (form.aiApiBaseUrl || '').trim() || '',
        aiDefaultModel: (form.aiDefaultModel || '').trim() || '',
      };
      await client.post('/store-settings', payload, { timeout: 15000 });
      setSaveMessage(t('settingsSaved'));
      await fetchSettings();
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isRtl = dir === 'rtl';
  const providerLabel = (p) => (isRtl ? p.labelAr : p.labelEn);

  return (
    <>
      <div
        className="console-settings-backdrop"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label={t('closeSettings')}
      />
      <aside className="console-settings-sidebar" aria-label={t('providerSettingsTitle')}>
        <div className="console-settings-header">
          <h2 className="console-settings-title">{t('providerSettingsTitle')}</h2>
          <button
            type="button"
            className="console-btn console-btn-close"
            onClick={onClose}
            aria-label={t('closeSettings')}
          >
            ×
          </button>
        </div>
        <div className="console-settings-body">
          {loading ? (
            <p className="console-settings-loading">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="console-settings-form">
              {error && (
                <div className="console-settings-error" role="alert">
                  {error}
                </div>
              )}
              {saveMessage && (
                <div className="console-settings-success" role="status">
                  {saveMessage}
                </div>
              )}

              <div className="console-settings-field">
                <label className="console-settings-label">
                  <input
                    type="checkbox"
                    checked={form.aiEnabled}
                    onChange={(e) => handleChange('aiEnabled', e.target.checked)}
                  />
                  <span>{t('aiEnabled')}</span>
                </label>
              </div>

              {typeof onCreativeModeChange === 'function' && (
                <div className="console-settings-field">
                  <label className="console-settings-label">
                    <input
                      type="checkbox"
                      checked={!!creativeMode}
                      onChange={(e) => {
                        const v = e.target.checked;
                        try { localStorage.setItem('orion_creative_mode', v ? 'true' : 'false'); } catch (_) {}
                        onCreativeModeChange(v);
                      }}
                    />
                    <span>{t('creativeMode')}</span>
                  </label>
                  <p className="console-settings-hint">{t('creativeModeHint')}</p>
                </div>
              )}

              <div className="console-settings-field">
                <label className="console-settings-label" htmlFor="orion-settings-provider">
                  {t('aiProvider')}
                </label>
                <select
                  id="orion-settings-provider"
                  className="console-settings-select"
                  value={form.aiProvider}
                  onChange={(e) => handleChange('aiProvider', e.target.value)}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {providerLabel(p)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="console-settings-field">
                <label className="console-settings-label" htmlFor="orion-settings-apikey">
                  {t('aiApiKey')}
                </label>
                <div className="console-settings-input-wrap">
                  <input
                    id="orion-settings-apikey"
                    type={showApiKey ? 'text' : 'password'}
                    className="console-settings-input"
                    value={form.aiApiKey}
                    onChange={(e) => handleChange('aiApiKey', e.target.value)}
                    placeholder={t('aiApiKeyPlaceholder')}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="console-settings-toggle-key"
                    onClick={() => setShowApiKey((v) => !v)}
                    aria-label={showApiKey ? t('hideApiKey') : t('showApiKey')}
                    title={showApiKey ? t('hideApiKey') : t('showApiKey')}
                  >
                    {showApiKey ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div className="console-settings-field">
                <label className="console-settings-label" htmlFor="orion-settings-baseurl">
                  {t('aiApiBaseUrl')}
                </label>
                <input
                  id="orion-settings-baseurl"
                  type="url"
                  className="console-settings-input"
                  value={form.aiApiBaseUrl}
                  onChange={(e) => handleChange('aiApiBaseUrl', e.target.value)}
                  placeholder="https://api.openai.com/v1"
                />
              </div>

              <div className="console-settings-field">
                <label className="console-settings-label" htmlFor="orion-settings-model">
                  {t('aiDefaultModel')}
                </label>
                <input
                  id="orion-settings-model"
                  type="text"
                  className="console-settings-input"
                  value={form.aiDefaultModel}
                  onChange={(e) => handleChange('aiDefaultModel', e.target.value)}
                  placeholder="gpt-4o-mini"
                />
              </div>

              <hr className="console-settings-divider" />
              <div className="console-settings-field">
                <label className="console-settings-label" htmlFor="orion-settings-user-instructions">
                  {t('userInstructions')}
                </label>
                {userInstructionsLoading ? (
                  <p className="console-settings-loading">{t('loading')}</p>
                ) : (
                  <>
                    <textarea
                      id="orion-settings-user-instructions"
                      className="console-settings-textarea"
                      value={userInstructions}
                      onChange={(e) => setUserInstructions(e.target.value.slice(0, USER_INSTRUCTIONS_MAX))}
                      placeholder={t('userInstructionsHint')}
                      rows={3}
                    />
                    <p className="console-settings-hint">{t('userInstructionsHint')}</p>
                    {userInstructionsMessage && (
                      <p className="console-settings-success" role="status">{userInstructionsMessage}</p>
                    )}
                    <button
                      type="button"
                      className="console-btn console-btn-primary"
                      onClick={handleSaveUserInstructions}
                      disabled={userInstructionsSaving}
                    >
                      {userInstructionsSaving ? t('saving') : t('saveSettings')}
                    </button>
                  </>
                )}
              </div>

              <div className="console-settings-actions">
                <button
                  type="button"
                  className="console-btn console-settings-cancel"
                  onClick={onClose}
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="console-btn console-btn-primary console-settings-save"
                  disabled={saving}
                >
                  {saving ? t('saving') : t('saveSettings')}
                </button>
              </div>
            </form>
          )}
        </div>
      </aside>
    </>
  );
}
